import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Eyebrow } from "./Reveal";

const faqs = [
  {
    q: "What is Romii?",
    a: "Romii is an AI-powered wearable companion designed to support individuals living with Alzheimer's disease, dementia, and other forms of cognitive decline. It provides contextual reassurance, intelligent assistance, caregiver connectivity, and everyday support to help people remain as confident, independent, and connected as possible.",
  },
  {
    q: "Is Romii a medical device?",
    a: "Romii is being developed as an intelligent assistive companion for cognitive care. It is not intended to diagnose, treat, cure, or replace professional medical care. Instead, it is designed to complement caregivers and healthcare professionals by supporting everyday wellbeing, reassurance, and communication.",
  },
  {
    q: "Who is Romii designed for?",
    a: "Romii is designed for people living with dementia, Alzheimer's disease, and other forms of cognitive decline, as well as their families, caregivers, clinicians, hospitals, memory care centres, senior living communities, and healthcare professionals seeking innovative assistive solutions.",
  },
  {
    q: "How does Romii help people living with dementia?",
    a: "Romii is designed to provide gentle, contextual reassurance throughout the day. It helps users remember important personal information, supports daily routines with reminders, encourages independence, provides conversational assistance, and helps reduce anxiety during moments of confusion while preserving dignity and confidence.",
  },
  {
    q: "How does Romii support caregivers?",
    a: "Romii helps caregivers stay connected and informed while reducing some of the emotional burden of constant reassurance. It is designed to complement caregiving by providing intelligent assistance, communication, and timely support whenever appropriate.",
  },
  {
    q: "Does Romii replace caregivers or healthcare professionals?",
    a: "No. Romii is designed to support human care, never replace it. Our vision is to strengthen the relationship between individuals, families, caregivers, and clinicians by providing thoughtful AI assistance that works alongside existing care.",
  },
  {
    q: "Is Romii currently available?",
    a: "Romii is currently in the advanced prototype stage. We are actively collaborating with clinicians, researchers, dementia specialists, caregivers, hospitals, and care institutions to refine the product through real-world feedback before broader deployment.",
  },
  {
    q: "Can I request a demonstration?",
    a: "Yes. We welcome demonstration requests from clinicians, hospitals, researchers, caregivers, senior living communities, memory care providers, and potential collaborators who would like to learn more about Romii and share their valuable feedback.",
  },
  {
    q: "Can I become an early collaborator or pilot partner?",
    a: "Absolutely. We are currently seeking collaborations with neurologists, psychiatrists, geriatricians, dementia specialists, hospitals, senior living communities, researchers, caregivers, and organizations interested in helping shape the future of cognitive care through thoughtful innovation.",
  },
  {
    q: "Why was Romii created?",
    a: "Romii was inspired by personal experiences with Alzheimer's disease and dementia within our own family. Watching loved ones struggle with confusion, dependence, and the loss of everyday confidence made us realize that technology could do much more than simply monitor people. It could reassure them, preserve their dignity, encourage independence, and strengthen the connection between individuals and those who care for them. That belief continues to guide every decision we make.",
  },
  {
    q: "How can I stay updated on Romii's progress?",
    a: "If you would like to receive updates about Romii, request a demonstration, explore collaboration opportunities, or simply follow our journey, we would be delighted to hear from you. Please reach out using the contact form below or email us directly.",
  },
  {
    q: "How does Romii protect privacy?",
    a: "Privacy and dignity are fundamental principles in the design of Romii. We are building the platform with a privacy-first approach, incorporating secure communication, encrypted data handling, and strict access controls. Personal information is used only to provide reassurance, safety, and care support — and families always remain in control of their data.",
  },
];

export default function FAQ() {
  return (
    <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow number="10" label="FAQ" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#111827]">
                Frequently Asked{" "}
                <span className="font-serif-italic text-[#7F9F43]">Questions.</span>
              </h2>
              <p className="mt-5 text-gray-500 text-base md:text-lg leading-relaxed max-w-sm">
                Everything you may want to know about Romii and our mission.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                {faqs.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`faq-${i}`}
                    className="rounded-2xl bg-white border border-gray-100 card-soft px-6 data-[state=open]:border-[#7F9F43]/30"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-[#111827] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-500 leading-relaxed text-sm md:text-base pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
